import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card'
import { Button } from '../ui/Button'
import { Loader2, AlertCircle, Download, RefreshCw } from 'lucide-react'

// Memoized metric card component
const MetricCard = React.memo(({ title, value, subtitle }) => (
  <Card>
    <CardContent className="p-3 sm:p-4">
      <div className="text-xl sm:text-2xl font-bold">{value}</div>
      <p className="text-xs text-muted-foreground">{title}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
))

MetricCard.displayName = 'MetricCard'

const ReportsTab = () => {
  const [metrics, setMetrics] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Memoized action handlers
  const handleDownloadReport = useCallback((reportType) => {
    // Implement download logic
  }, [])

  const handleRefresh = useCallback(() => {
    // Refresh data
    window.location.reload()
  }, [])

  // Fetch reports data
  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Mock data - replace with actual API call
        const mockMetrics = [
          {
            id: 'response-time',
            title: 'Avg Response Time (min)',
            value: '12.5',
            subtitle: 'Target: < 15 min'
          },
          {
            id: 'utilization',
            title: 'Fleet Utilization',
            value: '94.2%',
            subtitle: 'Above target: 90%'
          },
          {
            id: 'rating',
            title: 'Driver Rating',
            value: '4.7',
            subtitle: 'Out of 5.0'
          },
          {
            id: 'revenue',
            title: 'Monthly Revenue',
            value: '₹1.8L',
            subtitle: '+12% from last month'
          }
        ]
        
        setMetrics(mockMetrics)
      } catch (err) {
        setError('Failed to load reports data. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchReportsData()
  }, [])

  // Loading component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin mr-2" />
      <span className="text-gray-600">Loading reports...</span>
    </div>
  )

  // Error component
  const ErrorDisplay = ({ error, onRetry }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div className="flex items-center">
        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
        <div>
          <h3 className="text-sm font-medium text-red-800">Error loading reports</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="!pb-0 px-4 sm:px-6 pt-4 sm:pt-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif text-base sm:text-lg">Fleet Reports</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Performance metrics and operational insights.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleDownloadReport('summary')}
                disabled={isLoading}
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorDisplay error={error} onRetry={() => window.location.reload()} />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                {metrics.map((metric) => (
                  <MetricCard
                    key={metric.id}
                    title={metric.title}
                    value={metric.value}
                    subtitle={metric.subtitle}
                  />
                ))}
              </div>
              <div className="flex items-center justify-center h-48 sm:h-64 text-muted-foreground border-2 border-dashed rounded-lg p-4 text-center">
                <div className="flex flex-col items-center">
                  <span className="text-4xl mb-2">📊</span>
                  <span className="text-sm sm:text-base">Fleet Analytics</span>
                  <span className="text-xs text-muted-foreground mt-1">
                    Performance trends, utilization reports, and revenue tracking
                  </span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default ReportsTab
